import apiClient from './api.ts';

export interface UserListItem {
    id: number;
    username: string;
    email?: string;
    full_name: string;
    user_type: 'student' | 'teacher' | 'parent' | 'staff' | 'admin' | 'unknown';
}

export interface PaginatedUsersResponse {
    status: number;
    message: string;
    data: UserListItem[];
    pagination: {
        current_page: number;
        per_page: number;
        total_pages: number;
        total_records: number;
        has_next: boolean;
        has_previous: boolean;
    };
}

export interface CreateStaffUserPayload {
    username: string;
    email?: string;
    password: string;
    first_name: string;
    last_name: string;
    phone_number?: string;
    position?: string;
    department?: string;
    branch_id: string;
    role_ids: number[];
}

export interface StaffUserProfile {
    user_id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    position: string;
    department: string;
    branch_id: string;
    branch_name: string;
    roles: Array<{ id: number; name: string }>;
}

export interface UpdateStaffUserPayload {
    first_name: string;
    last_name: string;
    email?: string;
    phone_number?: string;
    position?: string;
    department?: string;
    branch_id: string;
    role_ids: number[];
}

export default {
    async getUsers(page: number = 1, limit: number = 20, search: string = '') {
        const params: Record<string, any> = { page, limit };
        if (search) params.search = search;
        return await apiClient.get<PaginatedUsersResponse>('users', { params });
    },

    async getUserById(id: number) {
        return await apiClient.get(`users/${id}`);
    },

    async createStaffUser(payload: CreateStaffUserPayload) {
        return await apiClient.post('users', payload);
    },

    // isActive=false returns the archived ("Recently Deleted") staff set.
    async getStaffUsers(isActive: boolean = true) {
        return await apiClient.get<{ status: number; data: StaffUserProfile[] }>('users/staff', {
            params: { is_active: isActive },
        });
    },

    // Archiving also revokes the account's login, not just the profile.
    async archiveStaffUser(userId: number) {
        return await apiClient.put(`users/staff/${userId}/archive`);
    },

    async unarchiveStaffUser(userId: number) {
        return await apiClient.put(`users/staff/${userId}/unarchive`);
    },

    async deleteStaffUser(userId: number) {
        return await apiClient.delete(`users/staff/${userId}`);
    },

    async updateStaffUser(userId: number, payload: UpdateStaffUserPayload) {
        return await apiClient.put(`users/staff/${userId}`, payload);
    },

    async adminResetPassword(userId: number, newPassword: string) {
        return await apiClient.post(`users/${userId}/reset-password`, {
            new_password: newPassword,
        });
    },
};
